import { useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { WarrantyServiceEntry } from '@econtractor/types';
import AppDatePicker from 'components/dateTime/AppDatePicker';
import spinnerTitle from 'components/others/spinnerTitle';
import SolarIcon from 'components/icons/SolarIcon';
import { getLocale } from 'utils/helpers/functions/getLocale';
import { UserNameItem } from 'modules/user';
import { todayIsoDate } from '../model/warrantyDates';

interface TabServiceProps {
    rows: WarrantyServiceEntry[];
    canEdit: boolean;
    busy: boolean;
    onAdd: (text: string, serviceDate: string) => Promise<boolean>;
}

const calendarDate = (entry: WarrantyServiceEntry): Date | null => {
    if (entry.serviceDate && /^\d{4}-\d{2}-\d{2}$/.test(entry.serviceDate)) {
        const [year, month, day] = entry.serviceDate.split('-').map(Number);
        const date = new Date(year, month - 1, day);
        return Number.isNaN(date.getTime()) ? null : date;
    }
    const created = new Date(Number(entry.createdAt));
    return Number.isNaN(created.getTime()) ? null : created;
};

const shownServiceDate = (entry: WarrantyServiceEntry, locale: string): { label: string; iso?: string } => {
    const shown = calendarDate(entry);
    if (!shown) {
        return { label: '—' };
    }
    const iso = entry.serviceDate && /^\d{4}-\d{2}-\d{2}$/.test(entry.serviceDate)
        ? entry.serviceDate
        : [
            shown.getFullYear(),
            String(shown.getMonth() + 1).padStart(2, '0'),
            String(shown.getDate()).padStart(2, '0'),
        ].join('-');
    return {
        label: shown.toLocaleDateString(locale, {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        }),
        iso,
    };
};

const TabService = ({ rows, canEdit, busy, onAdd }: TabServiceProps) => {
    const { t, i18n } = useTranslation('Warranties');
    const [text, setText] = useState('');
    const [date, setDate] = useState(todayIsoDate);
    const locale = getLocale(i18n.resolvedLanguage || i18n.language);
    return (
        <div>
            {canEdit && (
                <div className="mb-3">
                    <Form.Group className="mb-2">
                        <Form.Label className="text-muted" htmlFor="warranty-service-date">
                            {t('date')}
                        </Form.Label>
                        <AppDatePicker
                            id="warranty-service-date"
                            value={date}
                            disabled={busy}
                            onChange={setDate}
                        />
                    </Form.Group>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        maxLength={1000}
                        aria-label={t('comment')}
                        placeholder={t('comment')}
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        disabled={busy}
                    />
                    <div className="d-flex justify-content-end mt-2">
                        <Button
                            type="button"
                            variant="info"
                            size="sm"
                            disabled={busy || !text.trim() || !date}
                            onClick={async () => {
                                if (await onAdd(text, date)) {
                                    setText('');
                                    setDate(todayIsoDate());
                                }
                            }}
                        >
                            {spinnerTitle(t('addComment'), busy, t('saving'))}
                        </Button>
                    </div>
                </div>
            )}
            {!rows.length && (
                <div className="text-muted py-3 text-center">
                    <SolarIcon name="clock-history" className="me-2" />
                    {t('empty')}
                </div>
            )}
            {rows.map((entry) => {
                const shown = shownServiceDate(entry, locale);
                return (
                    <div key={entry.ID} className="border-bottom py-2">
                        <div className="d-flex align-items-start justify-content-between gap-3 mb-2">
                            <div className="min-w-0 flex-grow-1">
                                <UserNameItem
                                    smallMode
                                    user={{
                                        userName: entry.userName || '—',
                                        userPicture: entry.userPicture || '',
                                    }}
                                />
                            </div>
                            <time className="text-end flex-shrink-0" dateTime={shown.iso}>
                                <span className="d-block fw-medium text-body">{shown.label}</span>
                            </time>
                        </div>
                        <p className="mb-0 text-break" style={{ whiteSpace: 'pre-wrap' }}>
                            {entry.text}
                        </p>
                    </div>
                );
            })}
        </div>
    );
};

export default TabService;
